import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useSafeSearch } from "./router-safe";

export type BreadcrumbPage = { href: string; label: string };
type TrailEntry = { href: string; label: string | null };
type BreadcrumbTrail = {
  parents: BreadcrumbPage[];
  nameCurrentPage: (label: string, fallback: BreadcrumbPage) => void;
};

const TrailContext = createContext<BreadcrumbTrail | null>(null);
const MARKETPLACE_SECTIONS = new Set(["Jobseekers", "Services", "Companies", "Jobs", "Project Bidding", "RFQs", "Marketplace"]);
const LEGACY_SECTION_LABELS = new Map([
  ["Materials", "Marketplace"], ["People", "Jobseekers"], ["Workers", "Jobseekers"], ["Projects", "Project Bidding"],
]);
const DASHBOARD_VIEWS = new Map([
  ["services", "My Services"], ["jobs", "My Jobs"], ["projects", "My Projects"],
  ["rfqs", "My RFQs"], ["items", "My Listings"], ["companies", "My Company"], ["bids", "My Bids"],
]);
const AUTH_PAGES = new Set(["/login", "/signup", "/forgot-password", "/reset-password"]);
const DETAIL_PAGE_PATH = /^\/(?:s|c|j|project|rfq|item|u|p)\/[^/]+\/?$/;
const PAGE_LABELS: Partial<Record<string, string>> = {
  "/": "Home", "/saved": "Saved", "/search": "Search", "/dashboard": "Dashboard",
  "/messages": "Messages", "/contracts": "Contracts", "/settings": "Account Settings",
  "/wallet": "Wallet & Escrow", "/how-it-works": "How It Works", "/support": "Support",
};

function routeLabel(path: string, search: string): string | null {
  const params = new URLSearchParams(search);
  if (path === "/marketplace") {
    const requestedTab = params.get("tab") || "Services";
    const tab = LEGACY_SECTION_LABELS.get(requestedTab) ?? requestedTab;
    return MARKETPLACE_SECTIONS.has(tab) ? tab : "Services";
  }
  if (path === "/dashboard") {
    const view = params.get("view");
    return view ? DASHBOARD_VIEWS.get(view) ?? "Dashboard" : "Dashboard";
  }
  if (path.startsWith("/messages/")) return "Messages";
  if (path.startsWith("/u/") || path.startsWith("/p/")) return "Profile";
  return PAGE_LABELS[path] ?? null;
}

/** [marketplace.detail-trail](../../../docs/product/marketplace.md#marketplace-detail-trail): Keep the current detail journey, starting at its latest entry surface. */
export function BreadcrumbTrailProvider({ children }: { children: ReactNode }) {
  const [path] = useLocation();
  const search = useSafeSearch();
  const href = path + (search && !AUTH_PAGES.has(path) ? `?${search}` : "");
  const [entries, setEntries] = useState<TrailEntry[]>(() => [{ href, label: routeLabel(path, search) }]);

  if (entries[entries.length - 1]?.href !== href) {
    const existingIndex = entries.findIndex((entry) => entry.href === href);
    const current = { href, label: routeLabel(path, search) };
    // A browse/account/other top-level page starts a new journey. Only detail-to-detail
    // navigation extends it, so unrelated earlier visits never become breadcrumb parents.
    // Returning to a detail ancestor trims the trail; query changes replace that step.
    const samePath = entries[entries.length - 1]?.href.split("?", 1)[0] === path;
    const isAuthPage = AUTH_PAGES.has(path);
    setEntries(isAuthPage || !DETAIL_PAGE_PATH.test(path) ? [current] : existingIndex >= 0 ? entries.slice(0, existingIndex + 1)
      : samePath ? [...entries.slice(0, -1), current] : [...entries, current]);
  }

  const nameCurrentPage = useCallback((label: string, fallback: BreadcrumbPage) => {
    setEntries((previous) => {
      // A detail read finishing after navigation cannot rename the new page.
      if (previous[previous.length - 1]?.href !== href) return previous;
      const parents = previous.slice(0, -1);
      const hasParents = parents.some((entry) => entry.label !== null);
      if (previous[previous.length - 1]?.label === label && hasParents) return previous;
      return [...(hasParents ? parents : [fallback]), { href, label }];
    });
  }, [href]);
  const parents = entries.slice(0, -1).filter((entry): entry is BreadcrumbPage => entry.label !== null);
  return <TrailContext.Provider value={{ parents, nameCurrentPage }}>{children}</TrailContext.Provider>;
}

export function useBreadcrumbTrail(): BreadcrumbTrail | null {
  return useContext(TrailContext);
}
